import Debug from 'debug';
import { RequestError, AuthorizationError } from './Exceptions';
import {
  type FilterState,
  type OrganismState,
  status,
  type StatusActions,
  UIFilterStateKey,
  isUIFilterStateKey,
  FilterStateKey,
} from './types';
import { day, hour, minute, month } from '@/constants';
import {
  EnvironmentHelper,
  MetroUser,
  MetroHelper,
  MetroEvent,
  FeedItem,
  MetroContextType,
} from '@/classes';
import { apiCall, entriesOf } from '@/functions';

const debug = Debug('livefire:organism:util'); // eslint-disable-line new-cap

type OkResponse<T = unknown> = { status: 'ok'; data: T };
type ErrorResponse = { status: 'error'; error: unknown; response: Response | null };
type TimeoutResponse = { status: 'timeout'; error: unknown; response: Response | null };

export function isOkResponse(response: OkResponse | ErrorResponse): response is OkResponse {
  return response.status === 'ok';
}

export function isTimeoutResponse(
  response: OkResponse | ErrorResponse | TimeoutResponse,
): response is TimeoutResponse {
  return response.status === 'timeout';
}

export function isErrorResponse(
  response: OkResponse | ErrorResponse | TimeoutResponse,
): response is ErrorResponse {
  return response.status === 'error';
}

type AccountData = {
  clientId: string;
  subscriptionId: string;
  subscriptionStatus: string;
  userName: string;
};

/**
 * Retrieve account details and also check that the response is valid.
 *
 * Similar to getAccount(), but also checks the HTTP response code.
 *
 * @returns Return the promised JSON data
 */
export async function getAccountAndCheckResponse(
  authToken: string,
): Promise<OkResponse<AccountData> | ErrorResponse | TimeoutResponse> {
  if (!authToken) {
    return Promise.reject(new Error('No token specified'));
  }

  const prefix = EnvironmentHelper.getApiUrl();

  return apiCall(`${prefix}accounts`, {
    timeout: 5000,
    token: authToken,
  }).then((res) => {
    // all is good, try to return the JSON.
    if (res.ok) {
      return {
        status: 'ok',
        data: res.data as AccountData,
      };
    }

    let error = null;
    let response = null;
    if ('response' in res && res.response) {
      console.error('Account details responded with an error', {
        status: res.response.status,
        statusText: res.response.statusText,
      });
      // eslint-disable-next-line prefer-destructuring
      response = res.response;
      error = new RequestError(res.response.statusText);
      if (res.response.status === 403) {
        error = new AuthorizationError(res.response.statusText);
      }
    } else if ('error' in res && res.error) {
      // eslint-disable-next-line prefer-destructuring
      error = res.error;
    }

    return {
      status: res.timeout ? 'timeout' : 'error',
      error,
      response,
    };
  });
}

/**
 * General feed item filter.
 *
 * Filters the feed items based on the specified search term.
 */
export function filterFeedItems(feedItems: FeedItem[], searchTerm: string): FeedItem[] {
  if (!Array.isArray(feedItems)) {
    throw new Error(`Invalid feed items: must be an array, received [${typeof feedItems}]`);
  }

  if (typeof searchTerm !== 'string') {
    return feedItems;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase();

  return feedItems.filter((feedItem) => {
    try {
      if ('recipients' in feedItem && feedItem.recipients) {
        const recipientMatch = feedItem.recipients.some((recipient) => {
          const { email, name } = recipient;
          return (
            (name || '').toLowerCase().includes(normalizedSearchTerm) ||
            (email || '').toLowerCase().includes(normalizedSearchTerm)
          );
        });

        if (recipientMatch) {
          return true;
        }
      }

      const { recipientName, subject } = feedItem;

      if ((recipientName || '').toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }

      if ((subject || '').toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }
    } catch (error) {
      console.error('Error filtering feed item. Treating as a miss', { feedItem, error });
    }

    return false;
  });
}

/**
 * Filter feed items based on the provided selected item types.
 *
 * The `selectedItemTypes` argument is expected to be a dictionary of `livefireItemTypes`
 * mapped to a boolean value, indicating whether that item type is to be allowed (true) or
 * filtered out (false).
 *
 * @param feedItems - The feed items to filter.
 * @param selectedItemTypes - The item types to allow through the filter.
 *
 * @returns Returns the list of livefire feed items after having the filter applied.
 */
export function filterFeedItemsByItemType(
  feedItems: FeedItem[],
  selectedItemTypes: Partial<FilterState>,
): FeedItem[] {
  const itemTypes = Object.entries(selectedItemTypes)
    .filter(([key, value]) => value)
    .map(([key]) => key);

  if (!itemTypes.length) {
    return [];
  }

  return feedItems.filter((feedItem) => itemTypes.includes(feedItem.type));
}

/**
 * Return the filter state that is applicable to the UI.
 *
 * The type `FilterState` applies to _all item types_, but the filter list
 * only holds a subset of the types (`UIFilterStateKey`).
 */
export function limitFilterStateToValidUIFilterState(
  filterState: Partial<Record<FilterStateKey, boolean>>,
): Partial<Record<UIFilterStateKey, boolean>> {
  const entries = entriesOf(filterState);
  return entries.reduce((carry, entry) => {
    if (entry) {
      const [key, value] = entry;
      if (isUIFilterStateKey(key)) {
        carry[key] = value;
      }
    }
    return carry;
  }, {} as Partial<Record<UIFilterStateKey, boolean>>);
}

/**
 * Return necessary props to make an ErrorScreen retry-able based on state
 *
 * @param organismState Current organism state
 * @param dispatch Organism dispatch function
 * @param errorHandler Organism errorHandler property
 */
export function getRetryError(
  organismState: OrganismState,
  dispatch: React.Dispatch<StatusActions>,
  errorHandler: React.MouseEventHandler | null = null,
): [string, React.MouseEventHandler] | [null, null] {
  const retry = organismState.status === status.RETRY_ERROR;
  const actionText = retry ? 'Retry' : null;
  if (actionText) {
    return [
      actionText,
      (event: React.MouseEvent) => {
        dispatch({ type: 'reset' });
        errorHandler?.(event);
      },
    ];
  }
  return [null, null];
}

export function updateFeedItemTimestamp(
  items: FeedItem[],
  now: number,
): { items: FeedItem[]; updates: number } {
  // quick exit, there are no items.
  if (!items.length) {
    debug('no items to process');
    return { items, updates: 0 };
  }

  const almostOneMonthAgo = now - month;

  debug('time range', { now, almostOneMonthAgo });
  const updatable = items.filter((fi) => fi.eventTS >= almostOneMonthAgo);
  if (!updatable.length) {
    debug('no items to update');
    return { items, updates: 0 };
  }

  const lastMinute = now - minute;
  const lastHour = now - hour;
  const last24hours = now - day;
  const midnight = new Date(now).setHours(0, 0, 0, 0);

  let updates = 0;
  for (const item of updatable) {
    // If the item is within the last hour...
    if (item.eventTS > lastHour) {
      // ... update it once a minute.
      if ((item.updateTimerTick || 0) < lastMinute) {
        item.updateTimerTick = now;
        updates++;
      }

      continue;
    }

    // If the item is within the last 24 hours...
    if (item.eventTS > last24hours) {
      // ... update it once an hour.
      if ((item.updateTimerTick || 0) < lastHour) {
        item.updateTimerTick = now;
        updates++;
      }

      continue;
    }

    // Otherwise, update the item at least once per day if it is after midnight.
    if ((item.updateTimerTick || 0) < midnight) {
      item.updateTimerTick = now;
      updates++;
    }
  }

  debug('update result', { available: updatable.length, updates });
  return { items, updates };
}

/**
 * Returns a closure for tracking metrics.
 *
 * @param name Name(s) to send when the closure is triggered.
 * @param metroContext The current metro context.
 */
export function metric(name: string | string[], metroContext: MetroContextType): VoidFunction {
  const names = Array.isArray(name) ? name : [name];

  if (typeof metroContext.customHandler === 'function') {
    return () => {
      metroContext.customHandler?.(names);
    };
  }

  const user = new MetroUser(
    metroContext.clientId,
    metroContext.userName,
    metroContext.subscriptionId,
    metroContext.subscriptionStatus,
  );
  const helper = new MetroHelper(
    metroContext.appName || 'modulelibrary',
    EnvironmentHelper.getUppercaseEnvironment(),
    user,
  );

  const events = names.map((eventName) => new MetroEvent(eventName));
  return () => {
    helper.sendMetrics(events);
  };
}

let metricSearchTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Trigger a search metric when a user has filtered cards.
 */
export function metricSearch(metroContext: MetroContextType) {
  debug('Set search metric timeout');
  if (metricSearchTimeout !== null) {
    clearTimeout(metricSearchTimeout);
  }

  metricSearchTimeout = setTimeout(() => {
    debug('Searched metric sent');
    metric('livefire.search', metroContext)();
    metricSearchTimeout = null;
  }, 1000);
}

/**
 * Trigger a metric that was passed up a component chain.
 */
export function propagateMetric(metroContext: MetroContextType) {
  return (name: Parameters<typeof metric>[0]) => {
    debug('propagateMetric', name);
    metric(name, metroContext)();
  };
}
